import { Injectable } from '@angular/core';
import {User} from 'quest-atlas-angular-components';
import {environment} from '../../environments/environment';
import {BehaviorSubject, map, mergeWith, Observable, share, Subject, tap} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {switchMap} from 'rxjs/operators';
import {DomSanitizer} from '@angular/platform-browser';

export interface ProfileUpdate {
  username?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly BASE_URL = `${environment.API_URL}/`;
  private latestUserData: User;
  private manualUserDataUpdate$ = new Subject<User>();
  private triggerReloadUserInfo$ = new BehaviorSubject<void>(void 0);

  constructor(private http: HttpClient, private ds: DomSanitizer) {
  }

  getUserInfo$(): Observable<User> {
    return this.triggerReloadUserInfo$.pipe(
      switchMap(() => this.http.get<any>(`${this.BASE_URL}auth/current-user/`)),
      map(value => ({
        id: value.id,
        username: value.username,
        email: value.email,
        isFirstLogIn: value.is_first_time_login,
        isSuperUser: value.is_superuser,
        organizations: value.organizations ? value.organizations.map(org => ({
          id: org.organization__id,
          type: org.organization__type,
          name: org.organization__name,
          logo: this.ds.bypassSecurityTrustHtml(org.organization__logo),
          secondaryLogo: org.organization__secondary_logo,
          role: org.role,
        })) : [],
        leveling: {
          level: value.level,
          xp: value.experience_on_cur_level,
          xpToNextLevel: value.experience_to_new_level,
        },
        subscriptionPlan: value.subscription_plan ? {
          planName: value.subscription_plan.plan_name,
          startDate: new Date(value.subscription_plan.start_date),
          endDate: new Date(value.subscription_plan.end_date),
        } : null,
        firstName: value.first_name,
        lastName: value.last_name,
        phoneNumber: value.phone_number,
        avatarConfig: value.avatar_config,
        privacySettings: value.privacy_settings || {}
      })),
      tap(value => console.log('loaded user info', value)),
      mergeWith(this.manualUserDataUpdate$),
      tap(value => this.latestUserData = value),
      share(),
    );
  }

  deleteUser$(fullWipe = false): Observable<void> {
    const httpParams = fullWipe ? {params: {'full-wipe': 'true'}} : {};
    return this.http.delete<void>(`${this.BASE_URL}auth/current-user/delete`, {...httpParams});
  }

  triggerUserInfoReload() {
    this.triggerReloadUserInfo$.next(void 0);
  }

  updateProfile$(changes: ProfileUpdate): Observable<void> {
    const body: Record<string, string> = {};
    if (changes.username !== undefined) body['new_username'] = changes.username;
    if (changes.firstName !== undefined) body['first_name'] = changes.firstName;
    if (changes.lastName !== undefined) body['last_name'] = changes.lastName;
    if (changes.phoneNumber !== undefined) body['new_phone_number'] = changes.phoneNumber;
    return this.http.patch<void>(`${this.BASE_URL}auth/current-user/update`, body)
      .pipe(tap(() => {
        this.latestUserData = {
          ...this.latestUserData,
          ...(changes.username !== undefined ? {username: changes.username} : {}),
          ...(changes.firstName !== undefined ? {firstName: changes.firstName} : {}),
          ...(changes.lastName !== undefined ? {lastName: changes.lastName} : {}),
          ...(changes.phoneNumber !== undefined ? {phoneNumber: changes.phoneNumber} : {}),
        };
        this.manualUserDataUpdate$.next(this.latestUserData);
      }));
  }

  updatePrivacySettings$(settings: Record<string, any>): Observable<void> {
    return this.http.put<void>(`${this.BASE_URL}auth/update-privacy-settings`, settings)
      .pipe(tap(() => {
        this.latestUserData = {...this.latestUserData, privacySettings: settings};
        this.manualUserDataUpdate$.next(this.latestUserData);
      }));
  }

  getLatestUserData(): User {
    return { ...this.latestUserData };
  }

  findUser$(value: string, notContactsOnly = true): Observable<User> {
    const httpParams = new HttpParams()
      .set('search', value)
      .set('notContactsOnly', notContactsOnly.toString());
    return this.http.get<any>(`${this.BASE_URL}users/find`, {params: httpParams})
      .pipe(map((c) => {
        return {
          id: c.id,
          username: c.username,
          email: c.email,
          leveling: {
            level: c.level
          },
          avatar_config: c.avatar_config
        } as User;
      }));
  }
}
